// Waste Disposal Class and sub classes
import { v4 as uuidv4 } from "uuid";

// Waste Disposal Basic Info
export class WasteDisposalBasicInfo {
  constructor(data = {}) {
    this.id = data.id || uuidv4();
    this.clientName = data.client_name || "";
    this.disposalDate = data.disposal_date || "";
    this.generatorName = data.generator_name || "";
    this.contact = data.contact || "";
    this.phoneNumber = data.phone_number || "";
    this.faxNumber = data.fax_number || "";
    this.streetAddress = data.street_address || "";
    this.city = data.city || "";
    this.state = data.state || "";
    this.zipcode = data.zipcode || "";
    this.siteGenerator = data.site_generator || "";
    this.nameOfWaste = data.name_of_waste || "";
    this.processGenerating = data.process_generating || "";
    this.samplesTakenYes = data.samples_taken_yes || false;
    this.samplesTakenNo = data.samples_taken_no || false;
    this.samplesSentTo = data.samples_sent_to || "";
    this.chainOfCustodyYes = data.chain_of_custody_yes || false;
    this.chainOfCustodyNo = data.chain_of_custody_no || false;
    this.msdsYes = data.msds_yes || false;
    this.msdsNo = data.msds_no || false;
    this.locationOfContainers = data.location_of_containers || "";
    this.letterOfAgencyYes = data.letter_of_agency_yes || false;
    this.letterOfAgencyNo = data.letter_of_agency_no || false;
    this.letterOfAgencyNa = data.letter_of_agency_na || false;
    this.comments = data.comments || "";
  }

  // Updating the disposal date
  updateDisposalDate(date) {
    if (!date) {
      this.disposalDate = "";
    } else {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      this.disposalDate = `${year}-${month}-${day}`;
    }
  }

  // Updating text attributes
  updateTextAttribute(attribute, value) {
    if (!value) {
      this[attribute] = "";
    } else {
      this[attribute] = value;
    }
  }

  // Updating Samples Taken Yes and No functions
  updateSamplesTakenYes(value) {
    if (!value) {
      this.samplesTakenYes = false;
    } else {
      this.samplesTakenYes = true;
      this.samplesTakenNo = false;
    }
  }
  updateSamplesTakenNo(value) {
    if (!value) {
      this.samplesTakenNo = false;
    } else {
      this.samplesTakenNo = true;
      this.samplesTakenYes = false;
    }
  }

  // Updating Chain of Custody Yes and No functions
  updateChainOfCustodyYes(value) {
    if (!value) {
      this.chainOfCustodyYes = false;
    } else {
      this.chainOfCustodyYes = true;
      this.chainOfCustodyNo = false;
    }
  }

  updateChainOfCustodyNo(value) {
    if (!value) {
      this.chainOfCustodyNo = false;
    } else {
      this.chainOfCustodyNo = true;
      this.chainOfCustodyYes = false;
    }
  }

  // Updating MSDS Yes and No functions
  updateMsdsYes(value) {
    if (!value) {
      this.msdsYes = false;
    } else {
      this.msdsYes = true;
      this.msdsNo = false;
    }
  }

  updateMsdsNo(value) {
    if (!value) {
      this.msdsNo = false;
    } else {
      this.msdsNo = true;
      this.msdsYes = false;
    }
  }

  // Updating Letter of Agency Yes and No functions
  updateLetterOfAgencyYes(value) {
    if (!value) {
      this.letterOfAgencyYes = false;
    } else {
      this.letterOfAgencyYes = true;
      this.letterOfAgencyNo = false;
      this.letterOfAgencyNa = false;
    }
  }

  updateLetterOfAgencyNo(value) {
    if (!value) {
      this.letterOfAgencyNo = false;
    } else {
      this.letterOfAgencyNo = true;
      this.letterOfAgencyYes = false;
      this.letterOfAgencyNa = false;
    }
  }

  updateLetterOfAgencyNa(value) {
    if (!value) {
      this.letterOfAgencyNa = false;
    } else {
      this.letterOfAgencyNa = true;
      this.letterOfAgencyYes = false;
      this.letterOfAgencyNo = false;
    }
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.id,
      client_name: this.clientName,
      disposal_date: this.disposalDate,
      generator_name: this.generatorName,
      contact: this.contact,
      phone_number: this.phoneNumber,
      fax_number: this.faxNumber,
      street_address: this.streetAddress,
      city: this.city,
      state: this.state,
      zipcode: this.zipcode,
      site_generator: this.siteGenerator,
      name_of_waste: this.nameOfWaste,
      process_generating: this.processGenerating,
      samples_taken_yes: this.samplesTakenYes,
      samples_taken_no: this.samplesTakenNo,
      samples_sent_to: this.samplesSentTo,
      chain_of_custody_yes: this.chainOfCustodyYes,
      chain_of_custody_no: this.chainOfCustodyNo,
      msds_yes: this.msdsYes,
      msds_no: this.msdsNo,
      location_of_containers: this.locationOfContainers,
      letter_of_agency_yes: this.letterOfAgencyYes,
      letter_of_agency_no: this.letterOfAgencyNo,
      letter_of_agency_na: this.letterOfAgencyNa,
      comments: this.comments,
    };
  }

  // Helper for single and multi-field patches to be sent via websocket
  toPatch(fields) {
    const fieldMap = {
      clientName: "client_name",
      disposalDate: "disposal_date",
      generatorName: "generator_name",
      contact: "contact",
      phoneNumber: "phone_number",
      faxNumber: "fax_number",
      streetAddress: "street_address",
      city: "city",
      state: "state",
      zipcode: "zipcode",
      siteGenerator: "site_generator",
      nameOfWaste: "name_of_waste",
      processGenerating: "process_generating",
      samplesTakenYes: "samples_taken_yes",
      samplesTakenNo: "samples_taken_no",
      samplesSentTo: "samples_sent_to",
      chainOfCustodyYes: "chain_of_custody_yes",
      chainOfCustodyNo: "chain_of_custody_no",
      msdsYes: "msds_yes",
      msdsNo: "msds_no",
      locationOfContainers: "location_of_containers",
      letterOfAgencyYes: "letter_of_agency_yes",
      letterOfAgencyNo: "letter_of_agency_no",
      letterOfAgencyNa: "letter_of_agency_na",
      comments: "comments",
    };

    const patch = {
      patch_type: "waste_disposal",
      waste_disposal_id: this.id,
    };

    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }

    return patch;
  }
}

// Waste Disposal Container Info
export class WasteDisposalContainerInfo {
  constructor(data = {}) {
    this.rowId = data.id || uuidv4();
    this.container = data.container || "";
    this.amount = data.amount || "";
    this.wasteType = data.waste_type || "";
  }

  // Updating text attributes
  updateTextAttribute(attribute, value) {
    this[attribute] = value;
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.rowId,
      container: this.container,
      amount: this.amount,
      waste_type: this.wasteType,
    };
  }

  // Helper for single and multi-field patches to be sent via websocket
  toPatch(fields, action = "update", wasteDisposalId) {
    const fieldMap = {
      container: "container",
      amount: "amount",
      wasteType: "waste_type",
    };

    const patch = {
      patch_type: "waste_disposal_container_info",
      waste_disposal_id: wasteDisposalId,
      action,
      id: this.rowId,
    };

    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }

    return patch;
  }
}

// Waste Disposal Constituents
export class WasteDisposalConstituents {
  constructor(data = {}) {
    this.rowId = data.id || uuidv4();
    this.constituent = data.constituent || "";
    this.concentrationRange = data.concentration_range || "";
  }

  // Updating text attributes
  updateTextAttribute(attribute, value) {
    this[attribute] = value;
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.rowId,
      constituent: this.constituent,
      concentration_range: this.concentrationRange,
    };
  }

  // Helper for single and multi-field patches to be sent via websocket
  toPatch(fields, action = "update", wasteDisposalId) {
    const fieldMap = {
      constituent: "constituent",
      concentrationRange: "concentration_range",
    };

    const patch = {
      patch_type: "waste_disposal_constituents",
      waste_disposal_id: wasteDisposalId,
      action,
      id: this.rowId,
    };

    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }

    return patch;
  }
}

export class WasteDisposal {
  constructor(data = {}) {
    this.basicInfo = data.basic_info
      ? new WasteDisposalBasicInfo(data.basic_info)
      : new WasteDisposalBasicInfo();
    this.containerInfo = data.container_info
      ? data.container_info.map(
          (container) => new WasteDisposalContainerInfo(container)
        )
      : [new WasteDisposalContainerInfo()];
    this.constituents = data.constituents
      ? data.constituents.map(
          (constituent) => new WasteDisposalConstituents(constituent)
        )
      : [new WasteDisposalConstituents()];
  }

  // Adding a new container info object
  addContainerInfo(container) {
    this.containerInfo.push(new WasteDisposalContainerInfo(container));
  }

  // removing a container info object
  removeContainerInfo(rowId) {
    this.containerInfo = this.containerInfo.filter(
      (container) => container.rowId !== rowId
    );
  }

  // Updating container info
  updateContainerInfo(rowId, textAttribute, value) {
    this.containerInfo
      .find((container) => container.rowId === rowId)
      .updateTextAttribute(textAttribute, value);
  }

  // Adding a new constituent object
  addConstituent(constituent) {
    this.constituents.push(new WasteDisposalConstituents(constituent));
  }

  // removing a constituent object
  removeConstituent(rowId) {
    this.constituents = this.constituents.filter(
      (constituent) => constituent.rowId !== rowId
    );
  }

  // Updating constituent info
  updateConstituentInfo(rowId, textAttribute, value) {
    this.constituents
      .find((constituent) => constituent.rowId === rowId)
      .updateTextAttribute(textAttribute, value);
  }

  // To json for sending to the database
  toJson() {
    return {
      waste_disposal_info: this.basicInfo.toJson(),
      waste_disposal_table: this.containerInfo.map((container) =>
        container.toJson()
      ),
      waste_disposal_constituents_table: this.constituents.map((constituent) =>
        constituent.toJson()
      ),
    };
  }
}
